import React, { useState } from 'react';
import { API_URL } from '../config';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { FaFileInvoice, FaSpinner } from 'react-icons/fa';
import PurchaseOrderPDF from './PurchaseOrderPDF';
import { getCompanyDetails } from '../config/CompanyDetails';

// ============================================
// SUBBIE PURCHASE ORDER BUTTON
// Issues (or re-opens) the ONE purchase order for this (subcontractor x job) and downloads
// the branded PDF. Pressing it twice returns the SAME P.O. - the backend is idempotent, so
// a subbie is never sent two POs for the same work.
// ============================================
const PurchaseOrderButton = ({ jobId, subcontractorId, subbieName, iconOnly = false, onSuccess }) => {
  const [busy, setBusy] = useState(false);

  const handleGenerate = async () => {
    if (busy || !jobId || !subcontractorId) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({ job_id: jobId, subcontractor_id: subcontractorId })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not create the purchase order');

      const company = await getCompanyDetails();
      const blob = await pdf(
        <PurchaseOrderPDF
          purchaseOrder={data.purchase_order}
          job={data.job}
          subcontractor={data.subcontractor}
          items={data.items || []}
          company={company}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PurchaseOrder_${(data.purchase_order && data.purchase_order.po_number) || 'OPL-PO'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      const poNo = data.purchase_order && data.purchase_order.po_number;
      toast.success(`${data.created ? 'Purchase order created' : 'Purchase order'} ${poNo || ''} downloaded`);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('PO generate error:', err);
      toast.error(err.message || 'Could not generate the purchase order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleGenerate}
      disabled={busy}
      title={subbieName ? `Purchase order for ${subbieName} on this job` : 'Purchase order'}
      className={iconOnly
        ? 'text-gray-400 hover:text-green transition-colors p-1 disabled:opacity-50'
        : 'flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors disabled:opacity-50'}
    >
      {busy
        ? <FaSpinner className="animate-spin" size={iconOnly ? 12 : 14} />
        : <FaFileInvoice size={iconOnly ? 12 : 14} />}
      {!iconOnly && (busy ? 'Generating…' : 'Purchase Order')}
    </button>
  );
};

export default PurchaseOrderButton;
